import { isNil } from 'lodash';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { API } from '@shared/types/api';
import { JsonStorage } from '@shared/util/storage/JsonStorage';
import { ControlsState } from '../types';
import { useControlsState } from './ControlsProvider';
import { useViewTemplateState } from './ViewTemplateProvider';
import { isCollectionCriteriaEqual } from './isCollectionCriteriaEqual';
import { mapCollectionCriteriaToState } from './mapCollectionCriteriaToState';
import { mapStateToCollectionCriteria } from './mapStateToCollectionCriteria';

const COLLECTION_CRITERIA_TTL = 1000 * 60 * 60 * 24; // 1 day

interface Props {
  setIsModified: Dispatch<SetStateAction<boolean>>;
  resetState: (nextState: ControlsState) => void;
}

export function PreserveCollectionCriteria({ setIsModified, resetState }: Props) {
  const { savedViewTemplate } = useViewTemplateState();
  const storageKey = `viewTemplate-${savedViewTemplate.id}-collectionCriteria`;
  const { filters, sorts, groups, availableFilters, availableSorts, availableGroups } =
    useControlsState();

  useEffect(() => {
    const savedCriteria = savedViewTemplate.collectionCriteria ?? null;
    const updatedCriteria = mapStateToCollectionCriteria({ filters, sorts, groups });
    const isModified = !isCollectionCriteriaEqual(savedCriteria, updatedCriteria);
    setIsModified(isModified);

    if (isModified) {
      const timer = setTimeout(() => {
        // while changing views, can sometimes get a false positive, so delay saving
        JsonStorage.set(storageKey, updatedCriteria, COLLECTION_CRITERIA_TTL);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      JsonStorage.remove(storageKey);
    }
  }, [savedViewTemplate.collectionCriteria, filters, sorts, groups, setIsModified, storageKey]);

  useEffect(() => {
    // load collection criteria from storage
    async function loadCollectionCriteria() {
      const savedCollectionCriteria =
        await JsonStorage.get<NonNullable<API.ViewTemplateFragment['collectionCriteria']>>(
          storageKey,
        );
      if (!isNil(savedCollectionCriteria)) {
        const nextState = mapCollectionCriteriaToState(
          savedCollectionCriteria,
          availableFilters,
          availableSorts,
          availableGroups,
        );
        resetState(nextState);
      }
    }
    loadCollectionCriteria();
  }, [storageKey, availableFilters, availableSorts, availableGroups, resetState]);

  return null;
}
